import { trackEvent } from '@fs/zion-analytics'
import { baseUrl } from '@fs/zion-config'
import { processDownload } from '@fs/zion-image-viewer'
import {
  Button,
  Cell,
  Checkbox,
  CheckboxGroup,
  colors,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Grid,
  Image,
  Paragraph,
  Radio,
  RadioGroup,
  Row,
} from '@fs/zion-ui'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSlsStateContext } from '@fs/zion-sls'
import { NoticeLoading } from '@fs/zion-icon'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { generatePdfBlob, triggerFileDownload } from '../../utils/DownloadUtils'

export default function DownloadOverlay({
  downloadOverlay,
  displayHighlights,
  triggerStatusOverlay,
  citation,
  plainTextTranscript,
  query,
}) {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const sharedBrowserState = useSharedBrowserStateContext()
  const [includeImageDownload, setIncludeImageDownload] = useState('ImageAndHighlights')
  const [includeTranscriptDownload, setIncludeTranscriptDownload] = useState(query?.view === 'fullText')
  const [includeCitationDownload, setIncludeCitationDownload] = useState(true)
  const [isDownloading, setisDownloading] = useState(false)

  const thumbnails = sharedBrowserState.thumbnails

  const onDownload = async () => {
    try {
      setisDownloading(true)

      const imageUrl = thumbnails?.[sharedBrowserState.focusedIndex]?.srcSet?.[500]

      if (includeImageDownload === 'JPG Only' && imageUrl) {
        processDownload(imageUrl)
        triggerStatusOverlay(true)
        return
      }

      const citationText = t('metadata.citation', 'Citation')
      const url = `${baseUrl}/ark:/61903/${state?.currentImageArk}?view=${query?.view}`

      let content = ''
      if (query?.view === 'fullText' && includeTranscriptDownload) content += plainTextTranscript
      if (includeCitationDownload) content += `\n\n${citationText}:\n${citation}\n\n`

      const pdfBlob = await generatePdfBlob({
        content,
        imageUrl: includeImageDownload !== 'NoImage' ? imageUrl : undefined,
        highlights:
          includeImageDownload === 'ImageAndHighlights'
            ? displayHighlights?.ids?.map((id) => displayHighlights.highlights[id]?.position)
            : undefined,
        url,
      })

      triggerFileDownload(pdfBlob, `${t('full-text.transcript', 'Transcript')}.pdf`)

      trackEvent({ event_name: 'click_action', link_name: 'full-text-download-complete' })
      triggerStatusOverlay(true)
    } catch (e) {
      console.error('Download error:', e)
      triggerStatusOverlay(false)
    } finally {
      setisDownloading(false)
    }
  }

  return (
    <DialogOverlay
      {...downloadOverlay}
      title={t('full-text.download', 'Download')}
      subtitle={t('full-text.subheading', 'Customize your download option')}
      nonClosable
    >
      <Grid>
        <Image
          noCrop
          columns={4}
          alt={thumbnails?.[sharedBrowserState.focusedIndex]?.alt || ''}
          src={thumbnails?.[sharedBrowserState.focusedIndex]?.srcSet?.[0] || ''}
        />
        <Cell columns={8}>
          <RadioGroup disabled={isDownloading} label={t('full-text.record-image', 'Record Image')}>
            <Radio
              name="downloadOptions"
              wrapLabel
              checked={includeImageDownload === 'ImageAndHighlights'}
              value="ImageAndHighlights"
              label={t('full-text.pdf-record-image-with-highlights', 'PDF Record Image with Highlights')}
              onChange={() => setIncludeImageDownload('ImageAndHighlights')}
            />
            <Radio
              name="downloadOptions"
              wrapLabel
              checked={includeImageDownload === 'ImageWithoutHighlights'}
              value="ImageWithoutHighlights"
              label={t('full-text.pdf-record-image-with-no-highlights', 'PDF Record Image with No Highlights')}
              onChange={() => setIncludeImageDownload('ImageWithoutHighlights')}
            />
            <Radio
              name="downloadOptions"
              wrapLabel
              checked={includeImageDownload === 'JPG Only'}
              value="JPG Only"
              label={t('full-text.jpg-only', 'JPG Only')}
              onChange={() => setIncludeImageDownload('JPG Only')}
            />
            <Radio
              name="downloadOptions"
              wrapLabel
              checked={includeImageDownload === 'NoImage'}
              value="NoImage"
              label={t('full-text.no-record-image', 'No Record Image')}
              onChange={() => setIncludeImageDownload('NoImage')}
            />
          </RadioGroup>
          {includeImageDownload !== 'JPG Only' && (
            <CheckboxGroup disabled={isDownloading} label={t('full-text.include', 'Include:')}>
              {query?.view === 'fullText' && (
                <Checkbox
                  name="transcriptCheckbox"
                  label={t('full-text.transcription-text', 'Transcription')}
                  checked={includeTranscriptDownload}
                  onChange={() => setIncludeTranscriptDownload(!includeTranscriptDownload)}
                />
              )}
              <Checkbox
                name="citationCheckbox"
                label={t('full-text.citation-text', 'Citation')}
                checked={includeCitationDownload}
                onChange={() => setIncludeCitationDownload(!includeCitationDownload)}
              />
            </CheckboxGroup>
          )}
        </Cell>
      </Grid>
      {includeImageDownload === 'NoImage' && !includeTranscriptDownload && (
        <Row>
          <Paragraph size="xs">
            <span style={{ color: colors.red50 }}>
              {t(
                'full-text.select-image-or-transcript',
                'Please select either the transcript or record image for download.'
              )}
            </span>
          </Paragraph>
        </Row>
      )}
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button data-testid="full-text-cancel-download" onClick={downloadOverlay.close} disabled={isDownloading}>
            {t('full-text.cancel', 'Cancel')}
          </Button>
          <Button
            Icon={isDownloading ? NoticeLoading : null}
            data-testid="full-text-confirm-download"
            onClick={async () => {
              await onDownload()
              downloadOverlay.close()
            }}
            disabled={
              (includeImageDownload === 'NoImage' && !includeTranscriptDownload && !includeCitationDownload) ||
              isDownloading
            }
          >
            {t('full-text.download', 'Download')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
